<template>
  <div :state="workspace.status" :online-state="workspace.onlineStatus" class="headerCloudStates">
    <div class="deployCloud">
      <img :src="spaceIconStatus" width="48px" height="48px" />
    </div>

    <div class="deployLabel">
      <sub-status-labels v-if="workspace.subStatus" theme="light" :workspace="workspace" />

      <span v-else class="wrapLabel uppercase">
        <span class="deployStateLabel">{{ deployedState }}</span>
        <span v-if="isLoadingState" class="dot">.</span>
      </span>

      <span v-if="isDeployed" class="onlineApps uppercase">
        <span v-if="isOn">{{ appCounter.online }} / {{ appCounter.total }}</span>
        <span>{{ $t(`WorkspaceEdit.onlineStatus.${workspace.onlineStatus}`) }}</span>
      </span>

      <span v-if="isLoadingState" class="subStateLabel uppercase">
        <span>{{ $t(`workspaceDisplayStatus.${workspace.status}`) }}</span>
      </span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from "@vue/composition-api"
import { WorkspaceOnlineStatusEnum, WorkspaceTemplate } from "@/types/workspace"
import { workspaceStatusIcon } from "@/utils/cloud-state-icons"
import { WorkspaceStates, isOnline } from "@/config/workspace-states"
import { translate } from "@/plugins/i18n"
import SubStatusLabels from "@/views/workspaceEdit/components/preHeader/_SubStatusLabels.vue"

export default defineComponent({
  name: "HeaderCloudState",
  props: {
    workspace: {
      type: Object as PropType<WorkspaceTemplate>,
      required: true
    }
  },
  setup(props) {
    const deployedState = computed(() => {
      const status = props.workspace.status
      return translate(`workspaceStatus.${status}`)
    })

    const isDeployed = computed(() => {
      return WorkspaceStates.isDeployed(props.workspace.status)
    })

    const isOn = computed(() => {
      if (isDeployed.value) {
        return isOnline(props.workspace.onlineStatus)
      }
      return false
    })

    const isLoadingState = computed(() => {
      return WorkspaceStates.isLoading(props.workspace.status)
    })

    const appCounter = computed(() => {
      const appsOnline = appCount.value.online + serviceCount.value.online
      const appTotal = appCount.value.total + serviceCount.value.total

      return {
        online: appsOnline,
        total: appTotal
      }
    })

    const serviceCount = computed(() => {
      const deployed = props.workspace.schemaDeployed?.services
      const servicesOnline = deployed?.filter(service => service.onlineStatus === WorkspaceOnlineStatusEnum.Online)

      return {
        online: servicesOnline?.length ?? 0,
        total: deployed?.length ?? 0
      }
    })

    const appCount = computed(() => {
      const { Online } = WorkspaceOnlineStatusEnum
      const appsOn = _getAppsByStatus(Online)?.length
      const appProspect = props.workspace.schema.assets
      const appDeployed = props.workspace.schemaDeployed?.assets

      const totalApps = appProspect.filter(app => {
        return appDeployed?.some(deployedApp => deployedApp.id === app.id)
      }).length

      return {
        online: appsOn ?? 0,
        total: totalApps
      }
    })

    const spaceIconStatus = computed(() => {
      return workspaceStatusIcon(props.workspace, isOn.value)
    })

    const _getAppsByStatus = (status: WorkspaceOnlineStatusEnum) => {
      const deployedWorkspace = props.workspace as WorkspaceTemplate
      return deployedWorkspace?.schemaDeployed?.assets.filter(app => app.onlineStatus === status)
    }

    return {
      appCounter,
      serviceCount,
      deployedState,
      isLoadingState,
      spaceIconStatus,
      isDeployed,
      isOn
    }
  },
  components: {
    SubStatusLabels
  }
})
</script>
